/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { ComponentProps, ContextType, Ref, RefObject, JSX } from 'preact';
import { forwardRef } from 'preact/compat';
import { useCallback, useImperativeHandle, useMemo, useRef } from 'preact/hooks';

import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';

import { Label } from '#UNSAFE_Label';
import { ReadonlyTextField, ReadonlyTextFieldInput, TextField } from '#UNSAFE_TextField';
import { TextFieldContent } from '#UNSAFE_TextField/TextFieldContent';
import {
  CompactUserAssistance,
  InlineUserAssistance,
  type UserAssistanceDensityType
} from '#UNSAFE_UserAssistance';
import { useFocusableTextField, type FocusableHandle } from '#hooks/UNSAFE_useFocusableTextField';
import { useFormContext } from '#hooks/UNSAFE_useFormContext';
import { FormFieldContext } from '#hooks/UNSAFE_useFormFieldContext';
import type { FormVariantContext } from '#hooks/UNSAFE_useFormVariantContext';
import type { TestIdProps } from '#hooks/UNSAFE_useTestId';
import { useTextField } from '#hooks/UNSAFE_useTextField';
import { useUser } from '#hooks/UNSAFE_useUser';
import type { TextProps } from '#utils/UNSAFE_interpolations/text';
import type { Size } from '#utils/UNSAFE_size';
import type { LayoutColumnSpan } from '#utils/UNSAFE_styles/Layout';
import {
  formatTimeFromMasks,
  getLeadingZeroForHour,
  getTimeMasksFromLocaleAndOptions,
  replaceCustomMasksValuesWithPlaceholders
} from '#utils/UNSAFE_timeUtils';
import type { ValueUpdateDetail } from '#utils/UNSAFE_valueUpdateDetail';
import { TimeField } from './TimeField';
import type { Time, TimePlaceholders } from './types';
import { useTimeFieldState } from './useTimeFieldState';
import { mergeProps } from '#utils/UNSAFE_mergeProps';

type InlineUserAssistanceProps = ComponentProps<typeof InlineUserAssistance>;

type TextFieldContentProps = ComponentProps<typeof TextFieldContent>;

type Props = TestIdProps & {
  /**
   * The ID of an element (or space separated IDs of multiple elements) that
   * describes the input.
   */
  'aria-describedby'?: UnpackSignals<JSX.AriaAttributes>['aria-describedby'];

  /**
   * Text to provide guidance to help the user understand what data to enter.
   */
  assistiveText?: InlineUserAssistanceProps['assistiveText'];

  /**
   * Specifies how many columns to span in a FormLayout with direction === 'row'
   */
  columnSpan?: LayoutColumnSpan;

  /**
   * Use this property to provide content for the end slot, such as a time icon.
   */
  endContent?: TextFieldContentProps['endContent'];

  /**
   * Specifies the smallest time unit that is displayed by the component.
   * If set to minute, only hour and minute are shown.
   * If set to second then hour, minute, and second are shown.
   * If set to millisecond then hour, minute, second and millisecond are shown.
   */
  granularity?: 'minute' | 'second' | 'millisecond';

  /**
   * Specifies whether to always show a leading zero in the hour field when the hour is 1-digit,
   * or to hide a leading zero in the hour field when the hour is 1-digit, or whether to determine the leading zero by the user's locale.
   * By default, this is determined by the user's locale.
   */
  leadingZeroForHour?: 'show' | 'hide' | 'fromLocale';

  /**
   * Help source URL associated with the component.
   */
  helpSourceLink?: InlineUserAssistanceProps['helpSourceLink'];

  /**
   * Custom text to be rendered for the <code>helpSourceLink</code>.
   */
  helpSourceText?: InlineUserAssistanceProps['helpSourceText'];

  /**
   * Specifies whether the time displayed is a 12-hour clock with a day period or a 24-hour clock.
   * If set to 'fromLocale', the hour clock is determined by the user's locale, unless the masks property is also specified,
   * in which case the hour clock is 12 if the masks property has a dayPeriod (e.g., AM/PM) or 24 if the masks property does not have a dayPeriod.
   */
  hourClock?: '12' | '24' | 'fromLocale';

  /**
   * Specifies whether the component is disabled.
   */
  isDisabled?: boolean;

  /**
   * Specifies whether the component is readonly.
   */
  isReadonly?: boolean;

  /**
   * Specifies for accessibility purposes whether a value is required.
   *
   * Setting this property to <code>false</code> means that a value is not required to be
   * committed by the user. Setting this property to <code>true</code> means that a value
   * is required to be committed by the user.
   */
  isRequired?: boolean;

  /**
   * Specifies whether to show an indicator on screen that a value is required, for example
   * before the user has committed a value.
   */
  isRequiredShown?: InlineUserAssistanceProps['isRequiredShown'];

  /**
   * Specifies the label associated with the field.
   */
  label: string;

  /**
   * Specifies where the label is positioned relative to the field.
   */
  labelEdge?: 'inside' | 'start' | 'top' | 'none';

  /**
   * Specifies the width of the label when <code>labelEdge</code> is <code>"start"</code>.
   */
  labelStartWidth?: Size;

  /**
   * A ref to the component field. This can be used when you are composing with InputTimeMask
   * and need to provide an anchor ref to a floating component.
   */
  mainFieldRef?: RefObject<HTMLDivElement>;

  /**
   * Array-based representation of the segments used for time.
   * When specified, this option overrides the locale-specific formatting of the time: the separators, and the placement of the dayPeriod (AM/PM).
   *
   * For the SeparatorPlaceholder, the value is used as the separator between time segments. This is usually a ':' or a '.'.
   * For the DayPeriodPlaceholder the placement can be before the time or after the time.
   * The order of Hour/Minute/Seconds/Milliseconds cannot be changed and this will be enforced by the type.
   *
   * If the DayPeriodPlaceholder is in the masks, and the hourClock property is set to '24', an error is thrown
   * because these property values conflict.
   * If the DayPeriodPlaceholder is not in the masks, and the hourClock property is set to '12', an error is thrown
   * because these property values conflict.
   * If the masks property is specified and the granularity property does not match what is in the masks property, an error is thrown.
   */
  masks?: TimePlaceholders;

  /**
   * Messages to show on screen that are associated with the component.
   */
  messages?: InlineUserAssistanceProps['messages'];

  /**
   * Specifies how to align text within the field.
   */
  textAlign?: TextProps['textAlign'];

  /**
   * Specifies the density of the user assistance presentation. It can be set to:
   * <ul>
   * <li><code>'efficient'</code>: Show inline and reserve space to prevent layout reflow when user
   * assistance text is displayed.</li>
   * <li><code>'reflow'</code>: Show inline. Layout will reflow when text is displayed.</li>
   * <li><code>'compact'</code>: Messages, help, hints, and required will not be shown inline; they will show in a mode that keeps the screen more compact, like a popup for the messages, and a required icon to indicate Required. </li>
   * </ul>
   */
  userAssistanceDensity?: UserAssistanceDensityType;

  /**
   * The current value of the component. The value's hour is a 0-23 hour.
   * If the InputTimeMask's hour field is showing a 1-12 hour time, the
   * field's display value will be converted to a 0-23 hour, using the dayPeriod segment's value, which defaults to 'AM'.
   */
  value?: Time;

  /**
   * The style variant of the component.
   */
  variant?: ContextType<typeof FormVariantContext>;

  /**
   * Callback invoked when the user commits the entered value by blurring or hitting the enter key.
   * The value's hour is a 0-23 hour.
   *
   * If the InputTimeMask's hour field is showing a 1-12 hour time, the
   * field's display value will be converted to a 0-23 hour, using the dayPeriod segment's value, which defaults to 'AM'.
   *
   * Granularity will determine what fields in the value will be sent back in onInput and onCommit
   * as the user interacts with the component. For example, if granularity is 'minute' (the default),
   * then only the hour and minute fields will be shown to the user,
   * and the value sent back via onInput and onCommit will only include the fields the user has set.
   */
  onCommit?: (detail: ValueUpdateDetail<Time>) => void;

  /**
   * Callback invoked each time the user changes the value of a segment.
   *
   * For example, if the user
   * types '12' into the minute segment of an empty time field,
   * this callback will be called twice to change the minute
   * first to 1 and then 12.
   *
   * If the user types '1' into the hour segment of an empty time field,
   * this callback will be called once with the hour set to 1.
   * If the user then types '2', the callback will be called again. What it gets called with
   * depends on if the InputTimeMask is displaying a 1-12 hour time with a day period or a 0-23 hour time.
   * This is because the InputTimeMask's value's hour is stored as a 0-23 hour.
   *
   * If the user types in '12', and the time has an empty day period, the value's hour is 0, since the
   * dayPeriod defaults to 'AM' and 12 AM is converted to hour 0.
   * If the user types in '12', and the time does not have a day period, the value's hour is 12.
   *
   * If the user types in an hour that completes the segment, the other segments that are empty
   * are automatically filled with 0s, and the callback's value will reflect that, e.g.,
   * {hour: 0, minute: 0}.
   *
   * Granularity will determine what fields in the value will be sent back in onInput and onCommit
   * as the user interacts with the component. For example, if granularity is 'minute' (the default),
   * then only the hour and minute fields will be shown to the user,
   * and the value sent back via onInput and onCommit will only include the fields the user has set.
   */
  onInput: (detail: ValueUpdateDetail<Time>) => void;
};

/**
 * An InputTimeMask allows the user to enter, edit, or display a time value.
 */
export const InputTimeMask = forwardRef(
  (
    {
      'aria-describedby': ariaDescribedBy,
      assistiveText,
      columnSpan,
      endContent,
      granularity = 'minute',
      helpSourceLink,
      helpSourceText,
      hourClock = 'fromLocale',
      isDisabled: propIsDisabled,
      isReadonly: propIsReadonly,
      isRequired,
      isRequiredShown,
      label,
      labelEdge: propLabelEdge,
      labelStartWidth: propLabelStartWidth,
      leadingZeroForHour = 'fromLocale',
      mainFieldRef,
      messages,
      masks,
      testId,
      textAlign: propTextAlign,
      userAssistanceDensity: propUserAssistanceDensity,
      value,
      variant,
      onCommit,
      onInput
    }: Props,
    ref?: Ref<FocusableHandle>
  ) => {
    const {
      isDisabled: isFormDisabled,
      isReadonly: isFormReadonly,
      labelEdge: formLabelEdge,
      labelStartWidth: formLabelStartWidth,
      textAlign: formTextAlign,
      userAssistanceDensity: formUserAssistanceDensity
    } = useFormContext();
    // default to FormContext values if component properties are not specified
    const isDisabled = propIsDisabled ?? isFormDisabled;
    const isReadonly = propIsReadonly ?? isFormReadonly;
    const labelEdge = propLabelEdge ?? formLabelEdge;
    const labelStartWidth = propLabelStartWidth ?? formLabelStartWidth;
    // JET-72775 - Form control alignment in tables
    // default to 'start' if no text align is set
    const textAlign = propTextAlign ?? formTextAlign ?? 'start';
    const userAssistanceDensity = propUserAssistanceDensity ?? formUserAssistanceDensity;
    const enabledElementRef = useRef<HTMLDivElement>(null);
    const readonlyElementRef = useRef<HTMLDivElement>(null);

    const {
      focusProps,
      isFocused,
      methods: focusMethods
    } = useFocusableTextField<HTMLDivElement, HTMLDivElement>({
      isDisabled,
      isReadonly,
      enabledElementRef,
      readonlyElementRef
    });

    useImperativeHandle(ref!, () => focusMethods, [focusMethods]);

    const isComponentFocused = isFocused;

    const {
      baseId,
      formFieldContext,
      inputProps,
      labelProps: origLabelProps,
      textFieldProps,
      userAssistanceProps
    } = useTextField({
      ariaDescribedBy,
      helpSourceLink,
      helpSourceText,
      isDisabled,
      isFocused: isComponentFocused,
      isReadonly,
      isRequiredShown,
      labelEdge,
      messages,
      styleVariant: variant,
      userAssistanceDensity,
      value
    });

    // When using a div with role=group (i.e. TimeField), you need aria-labelledby instead of a
    // for-id association with a label. Remove forId and id and use aria-labelledby instead.
    const { forId, ...labelProps } = origLabelProps;
    const { id, onBlur: inputPropsOnBlur, ...timeFieldProps } = inputProps;

    const ariaLabelledBy = labelProps.id;
    // Because we don't use for-id, clicking the label does not automatically focus the field.
    // We have to do it manually.
    const onClick = useCallback(() => {
      enabledElementRef.current?.focus();
    }, [enabledElementRef]);

    const labelComp =
      labelEdge !== 'none' ? (
        <Label {...(!isDisabled && !isReadonly ? { onClick } : {})} {...labelProps}>
          {label}
        </Label>
      ) : undefined;

    const fieldLabelProps = {
      label: labelEdge !== 'none' ? labelComp : undefined,
      labelEdge: labelEdge !== 'none' ? labelEdge : undefined,
      labelStartWidth: labelEdge !== 'none' ? labelStartWidth : undefined,
      labelText: labelEdge === 'inside' && !isDisabled ? label : undefined
    };

    const ariaLabel = labelEdge === 'none' ? label : undefined;
    const hasInsideLabel = label !== undefined && labelEdge === 'inside';
    const isInlineDensity =
      userAssistanceDensity === 'efficient' || userAssistanceDensity === 'reflow';

    const inlineUserAssistance = isInlineDensity ? (
      isDisabled ? (
        // save space for user assistance if density is 'efficient', even though we don't
        // render user assistance for disabled fields
        userAssistanceDensity !== 'efficient' ? undefined : (
          <InlineUserAssistance
            userAssistanceDensity={userAssistanceDensity}
            {...userAssistanceProps}
          />
        )
      ) : isReadonly ? (
        <InlineUserAssistance
          fieldLabel={label}
          messages={messages}
          userAssistanceDensity={userAssistanceDensity}
          {...userAssistanceProps}
        />
      ) : (
        <InlineUserAssistance
          assistiveText={assistiveText}
          fieldLabel={label}
          helpSourceLink={helpSourceLink}
          helpSourceText={helpSourceText}
          messages={messages}
          isRequiredShown={isRequiredShown}
          userAssistanceDensity={userAssistanceDensity}
          {...userAssistanceProps}
        />
      )
    ) : undefined;

    const aRef = useRef<HTMLDivElement>(null);
    const anchorRef = mainFieldRef ?? aRef;

    const compactUserAssistance =
      userAssistanceDensity === 'compact' ? (
        <CompactUserAssistance
          anchorRef={anchorRef}
          messages={messages}
          assistiveText={assistiveText}
          {...userAssistanceProps}
        />
      ) : undefined;

    const { locale } = useUser();

    // Make sure the masks and properties do not conflict. This throws an error if they do conflict.
    validateTimeMasksAgainstProperties(masks, granularity, hourClock);

    // hour12 is true or false or undefined in TimeField because it is passed directly to Intl.DateTimeFormat and this is the api for that.
    let hour12: boolean | undefined;
    if (masks !== undefined && hourClock === 'fromLocale') {
      // If we have masks and the hourClock is set to 'fromLocale' which is the default as well, then we look at the masks
      // to set what hour12 we want to pass on to TimeField. If masks include a dayPeriod, then hour12 is true.
      const hasDayPeriod = masks.find((part) => part.type === 'dayPeriod') !== undefined;
      hour12 = hasDayPeriod;
    } else {
      hour12 = hourClock === '12' ? true : hourClock === '24' ? false : undefined;
    }

    // masks look like this:
    // const masks: TimePlaceholder = [
    //   {type: 'dayPeriod', value: 'am/pm'},
    //   {type: 'hour', value: 'hh'},
    //   {type: 'literal', value: ':'},
    //   {type: 'minute', value: 'mm'}
    // ]
    const timeMasks = useMemo(() => {
      // Determine the locale specific placeholder masks and separators in the correct order
      // for the locale, unless a custom set of masks was passed in. Each mask represents a
      // placeholder for a segment of a time. The hour/minute/second/millisecond's order never changes.
      // The dayPeriod segment can change: it can be at the start or at the end.
      // Memoize because we shouldn't need to recreate this data often.
      const placeholderMasks = masks
        ? replaceCustomMasksValuesWithPlaceholders(locale, masks) // no need to pass in hourClock. if masks has dayPeriod, then we use it since we checked before this if mask has dayPeriod and hour 12 is explicitly set to false, we throw an error.
        : // we get masks from locale.
          // hour12, if explicitly set, will take precedence.
          // This works: new Intl.DateTimeFormat('en-GB', {timeStyle: 'short', hour12: true}).format(datetime),
          getTimeMasksFromLocaleAndOptions(locale, granularity, hour12);

      return placeholderMasks;
    }, [granularity, hour12, locale, masks]);

    // We want the app dev to be able to force the hour to have a leading zero or force the hour to not have a leading zero.
    // This is useful for user preferences where the user wants the time to be in a particular format that is different than their locale.
    // Most commonly apps want to determine the leading zero from the locale, and therefore that is the default.
    const resolvedHasLeadingZeroForHour =
      leadingZeroForHour === 'fromLocale'
        ? getLeadingZeroForHour(locale)
        : leadingZeroForHour === 'show';

    const { state, dispatch } = useTimeFieldState({
      granularity,
      hasLeadingZeroForHour: resolvedHasLeadingZeroForHour,
      locale,
      timeMasks,
      value
    });

    // JET-73689: When we get onFocusOut on the time field, we delegate isCommitting to TimeField.
    // We do not want to commit if we are simply moving between segments.
    const onTimeFieldFocusOut = useCallback(
      (event: FocusEvent) => {
        !(event.currentTarget as HTMLElement).contains(event.relatedTarget as HTMLElement) &&
          dispatch({ actionType: 'commitOnBlur' }); // sets 'isCommitting' state. This might update the state, so we need to wait before calling onCommit.
      },
      [dispatch]
    );

    // merge the time field's onblurs.
    const { onBlur: handleTimeFieldBlur } = useMemo(() => {
      return mergeProps({ onBlur: onTimeFieldFocusOut }, { onBlur: inputPropsOnBlur });
    }, [inputPropsOnBlur, onTimeFieldFocusOut]);

    if (isReadonly) {
      return (
        <FormFieldContext.Provider value={formFieldContext}>
          <ReadonlyTextField
            role="presentation"
            columnSpan={columnSpan}
            compactUserAssistance={compactUserAssistance}
            inlineUserAssistance={inlineUserAssistance}
            onBlur={focusProps.onFocusOut}
            onFocus={focusProps.onFocusIn}
            ref={anchorRef}
            testId={testId}
            mainContent={
              <ReadonlyTextFieldInput
                aria-describedby={timeFieldProps['aria-describedby']}
                aria-label={ariaLabel}
                aria-labelledby={labelProps.id}
                as="div"
                elementRef={readonlyElementRef}
                textAlign={textAlign}
                value={
                  value === undefined
                    ? ''
                    : formatTimeFromMasks(
                        locale,
                        value,
                        granularity,
                        timeMasks,
                        resolvedHasLeadingZeroForHour
                      )
                }
                hasEmptyLabel={label === '' && labelEdge === 'none'}
                hasInsideLabel={hasInsideLabel}
                onBlur={inputProps.onBlur}
                onFocus={inputProps.onFocus}
              />
            }
            {...fieldLabelProps}></ReadonlyTextField>
        </FormFieldContext.Provider>
      );
    }

    // The main content inside TextField is a TimeField (not TextFieldInput) in this case.
    // We reuse the TextField building blocks and hooks, but recompose them.
    const mainContent = (
      <TimeField
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        baseId={baseId}
        dispatch={dispatch}
        fieldRef={enabledElementRef}
        granularity={granularity}
        hasInsideLabel={labelComp !== undefined && labelEdge === 'inside'}
        hasLeadingZeroForHour={resolvedHasLeadingZeroForHour}
        isRequired={isRequired}
        masks={timeMasks}
        onBlur={handleTimeFieldBlur}
        onCommit={onCommit}
        onInput={onInput}
        state={state}
        testId={testId ? testId + '_timeField' : undefined}
        textAlign={textAlign}
        value={value}
        {...timeFieldProps}
      />
    );

    return (
      <FormFieldContext.Provider value={formFieldContext}>
        <TextField
          columnSpan={columnSpan}
          compactUserAssistance={compactUserAssistance}
          endContent={endContent}
          inlineUserAssistance={inlineUserAssistance}
          mainContent={mainContent}
          onBlur={focusProps.onFocusOut}
          onFocus={focusProps.onFocusIn}
          mainFieldRef={anchorRef}
          testId={testId}
          {...textFieldProps}
          {...fieldLabelProps}
        />
      </FormFieldContext.Provider>
    );
  }
);

/**
 * This function checks the following:
 * - If both 'masks' and 'hourClock' are provided, it ensures that the 'hourClock' flag matches the presence of a 'dayPeriod' in the 'masks'.
 * - It verifies that the 'masks' contain the correct time placeholders based on the granularity.
 *
 * It throws an error if anything is not set correctly.
 * It never throws an error if masks is undefined; it just returns.
 * @param masks
 * @param granularity
 * @param hourClock
 * @throws if there is a conflict between masks and the granularity and hourClock properties.
 */
const validateTimeMasksAgainstProperties = (
  masks: TimePlaceholders | undefined,
  granularity: 'minute' | 'second' | 'millisecond',
  hourClock?: '12' | '24' | 'fromLocale'
) => {
  if (masks === undefined) {
    return;
  }
  // If the user passes in masks and hourClock, we look at masks to see what the hourClock should be.
  // If hourClock is passed in and it is different than what we expect, we throw an error.
  if (hourClock !== undefined && hourClock !== 'fromLocale') {
    const hasDayPeriod = masks.find((part) => part.type === 'dayPeriod') !== undefined;
    if (!hasDayPeriod && hourClock === '12') {
      throw new Error(
        `The masks object does not have a dayPeriod property. hourClock should be set to '24' to match masks.`
      );
    }
    if (hasDayPeriod && hourClock === '24') {
      throw new Error(
        `The masks object does have a dayPeriod property. hourClock should be set to '12' to match masks.`
      );
    }
  }
  // Type checking will make sure hour and minute are there since they are required.
  // Throw errors if masks does not contain the properties it should based on granularity.
  const hasSecond = masks.find((part) => part.type === 'second') !== undefined;
  const hasMillisecond = masks.find((part) => part.type === 'millisecond') !== undefined;
  const commonErrorMsg = `Change granularity to match the properties you have in the masks object.`;

  if (granularity === 'second' && !hasSecond) {
    const errorMsg = `granularity is second, but the masks does not contain the second property. 
      ${commonErrorMsg}`;
    throw new Error(errorMsg);
  }
  if (granularity === 'second' && hasMillisecond) {
    const errorMsg = `granularity is second, but the masks object contains the millisecond property. 
      ${commonErrorMsg}`;
    throw new Error(errorMsg);
  }
  if (granularity === 'millisecond' && (!hasSecond || !hasMillisecond)) {
    const errorMsg = `"Granularity is set to millisecond. The masks object should contain both the second and millisecond properties, but one or both are missing." 
      ${commonErrorMsg}`;
    throw new Error(errorMsg);
  }
  if (granularity === 'minute') {
    if (hasSecond || hasMillisecond) {
      throw new Error(`granularity is minute, but the masks object contains the second and/or millisecond property.
        ${commonErrorMsg}`);
    }
  }
};
